import { roundNumber, createOption } from '../Utils.js'
import { initialDebtRecord } from '../constants/initialState';

export default function incomeDebtReducer(draft, action) {
  
  const WEEK_CALC = 4.345;

  const addPayment = (index) => {
    const date = new Date();
    let amount = 0.00;
    const record = draft.records[index];
    if (record.status === 4) return; 
    if (draft.manual) {
      if (record.weekly) {
        amount = record.weeklyIndividual;
      } else {
        amount = record.monthlyIndividual;
      }
    } else {
      if (draft.weekly) {
        amount = record.weeklyOverall;
      } else {
        amount = record.monthlyOverall;
      }
    }
    if (amount >= Number(record.currentBalance)) {
      amount = record.currentBalance;
      record.status = 4;
    }
    record.paymentHistory.push(
      {
        date: date.toLocaleDateString('en-gb', { year:"numeric", month:"numeric", day:"numeric"}),
        debit: 0.00.toFixed(2),
        paymentCredit: roundNumber(amount).toFixed(2),
      }
    );
  }

  const calculatePercentage = (values, yearCalc) => {
    console.log("Percentage calc");
    values.forEach(v => {
      v.percentageOfDebt = v.amount>0&&v.status!==4?roundNumber((+v.amount/+draft.totalAmount)*100):0;
      v.monthlyOverall = v.percentageOfDebt>0?roundNumber((+draft.totalAmount/+draft.totalPeriod)*(+v.percentageOfDebt/100)):0;
      v.weeklyOverall = v.monthlyOverall>0?roundNumber(yearCalc?+v.monthlyOverall/WEEK_CALC:+v.monthlyOverall/4):0;
      v.currentBalance = v.status===4?0.00:roundNumber(+v.amount + +v.debitsAmount - +v.paymentsMade);
    });
    sortRecords(values, 'percentage', 2);
  }

  const calculateSnowballAvalanche = (values, yearCalc, remaining) => {
    console.log("Snowball calc/Avalanche");
    const withBalances = values.filter((d) => d.amount > 0);
    let paid = 0;
    withBalances.forEach(v => {
      let payment = Math.min(+v.currentBalance, +v.minimumPayment);
      v.monthlyOverall = payment;
      paid += payment;
      v.weeklyOverall = roundNumber(yearCalc?+v.monthlyOverall/WEEK_CALC:+v.monthlyOverall/4);
      v.currentBalance = v.status===4?0.00:roundNumber(+v.amount + +v.debitsAmount - +v.paymentsMade);
    });
    const withBalancesAfterPayments = values.filter((d) => d.amount > 0);
    let remainingAfterMin = remaining - paid;
    withBalancesAfterPayments.forEach(v => {
      let payment = Math.min(v.currentBalance, remainingAfterMin);
      v.monthlyOverall += payment;
      v.weeklyOverall = roundNumber(yearCalc?+v.monthlyOverall/WEEK_CALC:+v.monthlyOverall/4);
      remainingAfterMin -= payment;
      if (remainingAfterMin >= 0) {
        return;
      }
    });
  }

  const calculateAvalanchePeriod = (debtList, extra, yearCalc) => {
    // 1. Sort by highest interest rate first
    const sortedDebts = JSON.parse(JSON.stringify(debtList)).sort((a, b) => b.apr - a.apr);
    let totalMonths = 0;
    
    while (sortedDebts.some(d => +d.currentBalance > 0)) {
      totalMonths++;
      let monthlySurplus = extra;

      // 2. Apply minimum payments and calculate interest
      sortedDebts.forEach(debt => {
        let balance = Number(debt.currentBalance);
        let minimumPayment = Number(debt.minimumPayment);
        let apr = Number(debt.apr);

        if (balance > 0) {
          let monthlyInterest = (balance * apr) / 12;
          balance += monthlyInterest;
          
          // Pay the minimum or the remaining balance
          let payment = Math.min(balance, minimumPayment);
          balance -= payment;
        }
      });

      // 3. Apply the "Avalanche" extra payment to the highest-rate debt
      for (let debt of sortedDebts) {
        if (+debt.currentBalance > 0) {
          let extraToApply = Math.min(+debt.currentBalance, monthlySurplus);
          debt.currentBalance =+debt.currentBalance - extraToApply;
          monthlySurplus -= extraToApply;
          if (monthlySurplus <= 0) break;
        }
      }
    }
    return draft.weekly?totalMonths*(yearCalc?WEEK_CALC:4):totalMonths;
  }

  const calculateSnowballPeriod = (debtList, extra, yearCalc) => {
    // 1. Sort by smallest balance first (Snowball priority)
    const sortedDebts = JSON.parse(JSON.stringify(debtList)).sort((a, b) => +a.currentBalance - +b.currentBalance);
    let totalMonths = 0;

    while (sortedDebts.some(d => +d.currentBalance > 0)) {
      totalMonths++;
      let monthlySurplus = extra;

      // 2. Apply monthly interest and minimum payments to ALL active debts
      sortedDebts.forEach(debt => {
        let balance = Number(debt.currentBalance);
        let minimumPayment = Number(debt.minimumPayment);
        let apr = Number(debt.apr);
        if (balance > 0) {
          let monthlyInterest = (balance * apr) / 12;
          balance += monthlyInterest;
          
          let minPayment = Math.min(balance, minimumPayment);
          balance -= minPayment;
        } else if (balance === 0) {
          // 3. Rollover: If a debt is already paid, its minimum payment becomes extra surplus
          monthlySurplus += minimumPayment; 
        }
      });

      // 4. Apply total surplus (Extra + Rollovers) to the SMALLEST remaining balance
      for (let debt of sortedDebts) {
        if (+debt.currentBalance > 0) {
          let extraToApply = Math.min(+debt.currentBalance, monthlySurplus);
          debt.currentBalance = +debt.currentBalance - extraToApply;
          monthlySurplus -= extraToApply;
          if (monthlySurplus <= 0) break;
        }
      }
    }
    return draft.weekly?totalMonths*(yearCalc?WEEK_CALC:4):totalMonths;
  }
  
  const addValuesToRecords = (yearCalc, remaining, originalSort) =>  {
    let values = draft.records;
    if (!draft.manual) {
      switch (draft.repaymentType) {
        case 'PERCENTAGE': {
          calculatePercentage(values, yearCalc);
          break;
        }
        case 'SNOWBALL': {        
          values = sortRecords(values, 'minimumPayment');
          calculateSnowballAvalanche(values, yearCalc, remaining);
          draft.totalPeriod = calculateSnowballPeriod(values, remaining, yearCalc);
          break;
        }
        case 'AVALANCHE': {
          values = sortRecords(values, 'apr', 2);
          calculateSnowballAvalanche(values, yearCalc, remaining);
          draft.totalPeriod = calculateAvalanchePeriod(values, remaining, yearCalc);
          break;
        }
        default: console.log("Invalid Payment Type")
      }
    }
    draft.totalAmount = roundNumber(draft.records.map(item => item.status!==4?item.amount:0).reduce((prev, next) => 
      +prev + +next));     
    draft.totalPerMonthOverall = roundNumber(draft.records.reduce((acc, r) => 
      r.status===2 ? acc + +r.monthlyOverall : acc, 0));
    draft.totalPerWeekOverall = roundNumber(draft.records.reduce((acc, r) => 
      r.status===2 ? acc + +r.weeklyOverall : acc, 0));
    draft.totalPerMonthIndividual = roundNumber(draft.records.reduce((acc, r) => 
      r.status===2 ? acc + +r.monthlyIndividual : acc, 0));
    draft.totalPerWeekIndividual = roundNumber(draft.records.reduce((acc, r) => 
      r.status===2 ? acc + +r.weeklyIndividual : acc, 0));
    draft.totalCurrentBalance = roundNumber(+draft.totalAmount - draft.records.filter(item => item.status !== 4).map(item =>
      item.currentBalance).reduce((prev, next) => +prev + +next));
    draft.records = sortRecords(values, originalSort);
  };
  
  const calculatePayments = (index) => {
    const record = draft.records[index];
    record.noOfPayments = record.paymentHistory.length;
    record.paymentsMade = record.paymentHistory.length===0?(0.00):
      roundNumber(record.paymentHistory.map(item => item.paymentCredit).reduce((prev, next) =>
      +prev + +next));
    record.debitsAmount = record.paymentHistory.length===0?(0.00):
      roundNumber(record.paymentHistory.map(item => item.debit).reduce((prev, next) =>
      +prev + +next));
    record.currentBalance = record.paymentHistory.length===0?roundNumber(record.amount):
      roundNumber((record.amount - record.paymentsMade) + record.debitsAmount);
    draft.totalCurrentBalance = roundNumber(+draft.totalAmount - draft.records.map(item =>
      item.currentBalance).reduce((prev, next) => +prev + +next));
  }
  
  const sortRecords = (values, sortBy, order = 1) => {        
    switch(sortBy) {
      case 'current':
        if (order === 1) {
          return values.toSorted((a, b) => a.currentBalance - b.currentBalance);
        } else {
          return values.toSorted((a, b) => a.currentBalance - b.currentBalance).toReversed((a, b) => a.currentBalance - b.currentBalance);
        }
      case 'amount':
        if (order === 1) {
          return values.toSorted((a, b) => a.amount - b.amount);
        } else {
          return values.toSorted((a, b) => a.amount - b.amount).toReversed((a, b) => a.amount - b.amount);
        }
      case 'apr':
        if (order === 1) {
          return values.toSorted((a, b) => a.apr - b.apr);
        } else {
          return values.toSorted((a, b) => a.apr - b.apr).toReversed((a, b) => a.apr - b.apr);
        }
      case 'percentage':
        if (order === 1) {
          return values.toSorted((a, b) => a.percentageOfDebt - b.percentageOfDebt);
        } else {
          return values.toSorted((a, b) => a.percentageOfDebt - b.percentageOfDebt).toReversed((a, b) => a.percentageOfDebt - b.percentageOfDebt);
        }
      case 'minimumPayment':
        if (order === 1) {
          return values.toSorted((a, b) => a.minimumPayment - b.minimumPayment);
        } else {
          return values.toSorted((a, b) => a.minimumPayment - b.minimumPayment).toReversed((a, b) => a.minimumPayment - b.minimumPayment);
        }
      default:
        if (order === 1) {
          return values.toSorted((a, b) => a.status - b.status);
        } else {
          return values.toSorted((a, b) => a.status - b.status).toReversed((a, b) => a.status - b.status);
        }
    }
  }
  
  switch (action.type) {
    case 'MANUAL_SWITCH': {
      draft.manual = !draft.manual;
      draft.records.map(r => {
        if (!draft.manual) {
          if (r.status !== 4 ) r.status = 2;
        }
        return true;
      });
      break;
    }
    case 'OVERALL_WEEKLY': {
      draft.weekly = !draft.weekly;
      break;
    }
    case 'UPDATE_REPAYMENT_TYPE': {
      draft.repaymentType = action.repaymentType;
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'ADD_AUTO_PAYMENTS': {
      draft.records.map((item, index) => {
        addPayment(index);
        calculatePayments(index);
        return true;    
      });
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'CREATE_DESCRIPTION': {
      const newOption = createOption(action.inputValue, "DEBT");
      draft.descriptions.push(newOption);
      const values = [...draft.records];
      let updatedDescription = 'description';
      values[action.index][updatedDescription] = action.inputValue;
      break;
    }
    case 'CHANGE': {
      const values = [...draft.records];
      let updatedDescription = 'description';
      let updatedValue = '';
      if(null===action.event || action.event.target===undefined) {
        if(null!==action.event) {
          updatedValue = action.event.label;
        }
      } else {
        updatedDescription = action.event.target.name;
        updatedValue = action.event.target.value;
      }
      switch (updatedDescription) {
        case 'amount':
          values[action.index][updatedDescription] = roundNumber(parseFloat(updatedValue));
          break;
        case 'period':
        case 'periodWeekly':
        case 'monthlyIndividual':
        case 'weeklyIndividual': {
          const record = values[action.index];
          const balance = Number(record.currentBalanceCalc ? record.currentBalance : record.amount) || 0;
          const weekFactor = action.yearCalc ? WEEK_CALC : 4;
          const inputValue = Number(updatedValue) || 0;

          if (updatedDescription === 'period') {
            record.period = updatedValue;
            record.periodWeekly = roundNumber(inputValue * weekFactor);
            record.monthlyIndividual = inputValue > 0 ? roundNumber(balance / inputValue) : 0;
            record.weeklyIndividual = roundNumber(record.monthlyIndividual / weekFactor);
          } else if (updatedDescription === 'periodWeekly') {
            record.periodWeekly = updatedValue;
            record.period = roundNumber(inputValue / weekFactor);
            record.weeklyIndividual = inputValue > 0 ? roundNumber(balance / inputValue) : 0;
            record.monthlyIndividual = roundNumber(record.weeklyIndividual * weekFactor);
          } else if (updatedDescription === 'monthlyIndividual') {
            record.monthlyIndividual = updatedValue;
            record.weeklyIndividual = roundNumber(inputValue / weekFactor);
            record.period = inputValue > 0 ? roundNumber(balance / inputValue) : 0;
            record.periodWeekly = roundNumber(record.period * weekFactor);
          } else {
            record.weeklyIndividual = updatedValue;
            record.monthlyIndividual = roundNumber(inputValue * weekFactor);
            record.periodWeekly = inputValue > 0 ? roundNumber(balance / inputValue) : 0;
            record.period = roundNumber(record.periodWeekly / weekFactor);
          }
          break;
        }
        case 'status':
          values[action.index][updatedDescription] = Number(updatedValue);
          break;
        case 'currentBalanceCalc':
          values[action.index]['currentBalanceCalc'] = action.event.target.checked;
          break;
        default:
          values[action.index][updatedDescription] = updatedValue;
      }
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'BLUR_UPDATE': {
      const values = [...draft.records];
      let updatedValue = 0.00;
      const updatedDescription = action.event.target.name;
      updatedValue = action.event.target.value===""?0.00:parseFloat(action.event.target.value);
      const rounded = roundNumber(updatedValue);
      if (updatedDescription === 'debit' || updatedDescription === 'paymentCredit') {
        values[action.index][updatedDescription] = rounded.toFixed(2);
      }
      if (updatedDescription === "monthlyIndividual") {
        values[action.index]['monthlyIndividual'] = rounded.toFixed(2);
        values[action.index]['weeklyIndividual'] = Number(values[action.index]['weeklyIndividual']).toFixed(2);
      } else {
        values[action.index]['weeklyIndividual'] = rounded.toFixed(2);
        values[action.index]['monthlyIndividual'] = Number(values[action.index]['monthlyIndividual']).toFixed(2);
      }
      break;
    }
    case 'UPDATE_WEEKLY': {
      draft.records[action.index].weekly = !draft.records[action.index].weekly;
      break;
    }
    case 'CHANGE_PERIOD': {
      draft.totalPeriod = action.value;
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'CHANGE_TRANSACTION': {
      const record = draft.records[action.index];
      const values = [...record.paymentHistory];
      values[action.jndex][action.event.target.name] = 
          action.event.target.value;
      calculatePayments(action.index);
      break;
    }
    case 'ADD_TRANSACTION': {
      addPayment(action.index);
      calculatePayments(action.index);
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'REMOVE_TRANSACTION': {
      const record = draft.records[action.index];
      const values = [...record.paymentHistory];
      values.splice(action.jndex, 1);
      record.paymentHistory = values;
      if (record.status === 4) {
        record.status = 2;
      }
      addValuesToRecords(draft.yearCalc, action.remaining, action.sortBy);
      calculatePayments(action.index);
      break;
    }
    case 'UPDATE': {
      const values = [...draft.records];
      let updatedValue = 0.00;
      const updatedDescription = action.event.target.name;
      updatedValue = action.event.target.value===""?0.00:parseFloat(action.event.target.value);
      const rounded = roundNumber(updatedValue);
      values[action.index][updatedDescription] = rounded.toFixed(2);
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'ADD': {
      const values = [
      ...draft.records.slice(0, action.index),
        initialDebtRecord,
        ...draft.records.slice(action.index)
      ];
      draft.records = values;
      draft.monthlyTotal = roundNumber(draft.records.map(item => item.monthlyAmount).reduce((prev, next) => 
        prev + next));
      draft.weeklyTotal = roundNumber(draft.records.map(item => item.weeklyAmount).reduce((prev, next) => 
        prev + next));
      break;
    }
    case 'REMOVE': {
      const values = [...draft.records];
      values.splice(action.index, 1);
      draft.records = values;
      draft.monthlyTotal = roundNumber(draft.records.map(item => item.monthlyAmount).reduce((prev, next) => 
        prev + next));
      draft.weeklyTotal = roundNumber(draft.records.map(item => item.weeklyAmount).reduce((prev, next) => 
        prev + next));
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'SORT': {
      const values = [...draft.records];
      const split = action.sortBy.split(" ");
      draft.records = sortRecords(values, split[0], split.length>1?+split[1]:1);
      break;
    }
    case 'UPDATE_SAVED_DEBT': {
      draft.id = action.debt.id;
      draft.manual = action.debt.manual;
      draft.weekly = action.debt.weekly;
      draft.totalAmount = action.debt.totalAmount;
      draft.totalCurrentBalance = action.debt.totalCurrentBalance;
      draft.totalPeriod = action.debt.totalPeriod;
      draft.totalPerMonthOverall = action.debt.totalPerMonthOverall;
      draft.totalPerWeekOverall = action.debt.totalPerWeekOverall;
      draft.totalPerMonthIndividual = action.debt.totalPerMonthIndividual;
      draft.totalPerWeekIndividual = action.debt.totalPerWeekIndividual;
      draft.records = action.debt.records;
      draft.descriptions = action.descriptions;

      draft.monthlyTotal = roundNumber(draft.records.map(item => item.monthlyAmount).reduce((prev, next) => 
        prev + next));
      draft.weeklyTotal = roundNumber(draft.records.map(item => item.weeklyAmount).reduce((prev, next) => 
        prev + next));
      for (let i = 0; i < draft.records.length; i++) {
        calculatePayments(i);
      }
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    case 'RELOAD': {
      draft = action.data;
      break;
    }
    case 'REFRESH': {
      addValuesToRecords(action.yearCalc, action.remaining, action.sortBy);
      break;
    }
    default: {
      console.log("No action")
    }
  }
  return draft;
}
